import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeEvery, takeLatest, takeLeading } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { getDefaultHeaders } from "utils/helpers";
import { getPaginationMeta } from "utils/helpers/pagination";
import { request } from "utils/request";

import {
  Filter,
  FilterKey,
  ManufacturerModelForm,
  ManufacturerModelListResponse,
  ModelFormDropDownKey,
} from "../types";
import {
  selectDropDownsOptions,
  selectManufacturerModelForm,
  selectManufacturerModelsPage,
  selectManufacturerModelsPerPage,
  selectSelectedId,
} from "./selector";
import { manufacturerModelActions } from "./slice";

let isFetchingList = false;

const fetchingFormDropDowns: Record<ModelFormDropDownKey, boolean> = {
  manufacturers: false,
  superModels: false,
};

function* handleGetManufacturerModelsList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const page: number = yield select(selectManufacturerModelsPage);
    const perPage: number = yield select(selectManufacturerModelsPerPage);

    const response: ManufacturerModelListResponse = yield call(
      request,
      `${API_URL}/manufacturer-model?page=${page}&perPage=${perPage}`,
      { method: "GET", headers: getDefaultHeaders() }
    );

    const data = response?.data || [];
    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { data, meta };

    if (page === 1) {
      yield put(manufacturerModelActions.getModelsSuccess(payload));
    } else {
      yield put(manufacturerModelActions.getNextPageItemsSuccess(payload));
    }
  } catch (error) {
    const page: number = yield select(selectManufacturerModelsPage);
    if (page === 1) {
      yield put(
        manufacturerModelActions.getManufacturerModelsListFailure(
          (error as Error).message
        )
      );
    } else {
      yield put(
        manufacturerModelActions.getNextPageItemsFailure(
          (error as Error).message
        )
      );
    }
  } finally {
    isFetchingList = false;
  }
}

function* createManufacturerModel(action: { payload: ManufacturerModelForm }) {
  try {
    const form = action.payload;
    const payload = {
      ...form,
      manufacturerID: Number(form.manufacturerID),
      year: Number(form.year),
      chassisWidth: Number(form.chassisWidth),
      chassisHeight: Number(form.chassisHeight),
      maxWidth: Number(form.maxWidth),
      frontExtension: Number(form.frontExtension),
      licenseWeight: Number(form.licenseWeight),
      chassisWeight: Number(form.chassisWeight),
      frontWeelBase: Number(form.frontWeelBase),
    };
    const response = yield call(request, `${API_URL}/manufacturer-model`, {
      method: "POST",
      headers: getDefaultHeaders(),
      body: JSON.stringify(payload),
    });
    yield put(
      manufacturerModelActions.createManufacturerModelSuccess(response.data)
    );
  } catch (error) {
    yield put(manufacturerModelActions.createManufacturerModelFailure());
  }
}

function* getModelDetailsSaga(action: PayloadAction<number>) {
  try {
    const id = action.payload;
    const response = yield call(
      request,
      `${API_URL}/manufacturer-model/${id}`,
      { method: "GET", headers: getDefaultHeaders() }
    );
    yield put(manufacturerModelActions.getModelDetailsSuccess(response.data));
  } catch (error) {
    yield put(manufacturerModelActions.getModelDetailsFailure());
  }
}

function* updateModelSaga() {
  try {
    const id: number = yield select(selectSelectedId);
    const form = yield select(selectManufacturerModelForm);
    const payload = {
      ...form,
      manufacturerID: Number(form.manufacturerID),
      year: Number(form.year),
      chassisWidth: Number(form.chassisWidth),
      chassisHeight: Number(form.chassisHeight),
      maxWidth: Number(form.maxWidth),
      frontExtension: Number(form.frontExtension),
      licenseWeight: Number(form.licenseWeight),
      chassisWeight: Number(form.chassisWeight),
      frontWeelBase: Number(form.frontWeelBase),
    };
    const response = yield call(
      request,
      `${API_URL}/manufacturer-model/${id}`,
      {
        method: "PATCH",
        headers: getDefaultHeaders(),
        body: JSON.stringify(payload),
      }
    );
    yield put(manufacturerModelActions.updateModelSuccess(response.data));
  } catch (error) {
    yield put(manufacturerModelActions.updateModelFailure());
  }
}

function* deleteModelSaga() {
  try {
    const id: number = yield select(selectSelectedId);
    yield call(request, `${API_URL}/manufacturer-model/${id}`, {
      method: "DELETE",
      headers: getDefaultHeaders(),
    });
    yield put(manufacturerModelActions.deleteModelSuccess(id));
  } catch (error) {
    yield put(manufacturerModelActions.deleteModelFailure());
  }
}

function* getFilterOptions(filter: FilterKey) {
  try {
    const response = yield call(
      request,
      `${API_URL}/manufacturer-model/filter-options?type=${filter}`,
      { method: "GET", headers: getDefaultHeaders() }
    );
    const options = response?.data?.options || [];

    switch (filter) {
      case Filter.Year:
        yield put(manufacturerModelActions.getYearsOptionsSuccess(options));
        break;
      case Filter.LicenseWeight:
        yield put(manufacturerModelActions.getweightOptionsSuccess(options));
        break;
      case Filter.SuperModel:
        yield put(
          manufacturerModelActions.getSuperModelsOptionsSuccess(options)
        );
        break;
      case Filter.Manufacturer:
        yield put(
          manufacturerModelActions.getManufacturerOptionsSuccess(options)
        );
        break;
    }
  } catch (error) {
    yield put(manufacturerModelActions.getFilterOptionsFailure());
  }
}

function* handleGetFormDropDownList(
  action: PayloadAction<ModelFormDropDownKey>
) {
  const key = action.payload;
  if (fetchingFormDropDowns[key]) return;
  fetchingFormDropDowns[key] = true;

  try {
    const dropDowns = yield select(selectDropDownsOptions);
    const { page, perPage } = dropDowns[key];

    const url =
      key === "manufacturers"
        ? `${API_URL}/manufacturer?page=${page}&perPage=${perPage}`
        : `${API_URL}/super-model/list?page=${page}&perPage=${perPage}`;

    const response = yield call(request, url, {
      method: "GET",
      headers: getDefaultHeaders(),
    });

    const data = response?.data || [];
    const items =
      key === "manufacturers"
        ? data.map(
            (row: { menufecturerID: number; menufecturerName: string }) => ({
              id: row.menufecturerID,
              name: row.menufecturerName,
            })
          )
        : data.map(
            (row: { superModelId: number; superModelName?: string }) => ({
              id: row.superModelId,
              name: row.superModelName || "",
            })
          );

    const filtered = items.filter(
      (item: { id: number; name: string }) => item.id && item.name
    );
    const meta = getPaginationMeta(page, perPage, filtered, response?.meta);
    const payload = { key, data: filtered, meta };

    yield put(
      page === 1
        ? manufacturerModelActions.getFormDropDownListSuccess(payload)
        : manufacturerModelActions.getFormDropDownNextPageSuccess(payload)
    );
  } catch (error) {
    const message = (error as Error).message;
    const dropDowns = yield select(selectDropDownsOptions);

    yield put(
      dropDowns[key].page === 1
        ? manufacturerModelActions.getFormDropDownListError({ key, message })
        : manufacturerModelActions.getFormDropDownNextPageError({
            key,
            message,
          })
    );
  } finally {
    fetchingFormDropDowns[key] = false;
  }
}

export function* manufacturerModelSaga() {
  yield takeLeading(
    manufacturerModelActions.getManufacturerModelsList.type,
    handleGetManufacturerModelsList
  );
  yield takeLeading(
    manufacturerModelActions.getNextPageItems.type,
    handleGetManufacturerModelsList
  );
  yield takeLatest(
    manufacturerModelActions.addManufacturerModel,
    createManufacturerModel
  );
  yield takeLatest(
    manufacturerModelActions.getModelDetails.type,
    getModelDetailsSaga
  );
  yield takeLatest(manufacturerModelActions.updateModel.type, updateModelSaga);
  yield takeLatest(manufacturerModelActions.deleteModel.type, deleteModelSaga);
  yield takeLatest(
    manufacturerModelActions.getFilterOptions.type,
    getFilterOptions,
    Filter.Year
  );
  yield takeLatest(
    manufacturerModelActions.getFilterOptions.type,
    getFilterOptions,
    Filter.LicenseWeight
  );
  yield takeLatest(
    manufacturerModelActions.getFilterOptions.type,
    getFilterOptions,
    Filter.Manufacturer
  );
  yield takeLatest(
    manufacturerModelActions.getFilterOptions.type,
    getFilterOptions,
    Filter.SuperModel
  );
  yield takeEvery(
    manufacturerModelActions.getFormDropDownList.type,
    handleGetFormDropDownList
  );
  yield takeEvery(
    manufacturerModelActions.getFormDropDownNextPage.type,
    handleGetFormDropDownList
  );
}
